import { ref, provide, inject } from 'vue';
import moment from 'moment';
import { getOtsResultsByJobId, getCustomerStatus } from '../api/insightsApi';
import { llmInsight, CustomerStatus, POLLING_CONFIG } from '../types/insight';
import DDLogs from '../DDLogs';
import { getisFinishedLoadingCashflow } from '../api/cashflowApi';

interface UseInsights {
  insights: llmInsight[];
  isPolling: boolean;
  startPollingByJobId: (
    jobId: string,
    maxAttempts: number,
    interval: number,
    onSuccess: () => void,
    handleError: () => void,
    onStatusUpdate?: (status: CustomerStatus) => void
  ) => void;
  populateInsights: (jobId: string) => Promise<llmInsight[]>;
  updateIsPolling: (value: boolean) => void;
}

const insights = ref<llmInsight[]>([]);
const isPolling = ref(false);

const updateIsPolling = (value: boolean) => {
  isPolling.value = value;
};

/**
 * Starts polling for OTS results by job ID with status checking
 * This function implements a comprehensive polling strategy that:
 * 1. Checks customer status to detect failures or timeouts
 * 2. Verifies cashflow calculation completion
 * 3. Polls for actual insights results
 * 4. Handles errors gracefully with proper logging
 */
const startPollingByJobId = (
  jobId: string,
  maxAttempts: number,
  interval: number,
  onSuccess: () => void,
  handleError: () => void,
  onStatusUpdate?: (status: CustomerStatus) => void,
) => {
  let attempts = 0;

  isPolling.value = true;

  const checkOtsResults = async () => {
    attempts++;

    try {
      // Check customer status to determine if calculation is progressing normally
      const customerStatus: CustomerStatus = await getCustomerStatus();

      // Update progress if callback is provided
      if (onStatusUpdate && typeof onStatusUpdate === 'function') {
        onStatusUpdate(customerStatus);
      }

      // Handle failed status - only stop for actual failures, not timeouts
      // If status is 'fail', consider it failed. But if stuck in 'start', just log and continue
      const updateTime = customerStatus.updatedAt || customerStatus.lastUpdated;
      const isTimedOut = customerStatus.status === 'start'
        && updateTime && moment().diff(moment(updateTime)) > POLLING_CONFIG.TIMEOUT_MS;

      // Only stop polling for actual failures, not timeouts
      if (customerStatus.status === 'fail') {
        const errorContext = 'Customer status marked as failed';

        DDLogs.error('Customer status check failed', {
          status: customerStatus.status,
          updatedAt: updateTime,
          context: errorContext,
          jobId,
        });
        stopPolling();
        try {
          handleError();
        } catch (error) {
          console.error('Error in handleError callback:', error);
          DDLogs.error('HandleError callback failed', { originalError: error, jobId });
        }
        return;
      }

      // For timeout: log warning but continue polling
      if (isTimedOut) {
        DDLogs.warn('Customer status timeout - continuing to poll', {
          status: customerStatus.status,
          updatedAt: updateTime,
          timeoutDuration: moment().diff(moment(updateTime)),
          jobId,
        });
        // Don't return - continue with polling
      }

      const isFinishedLoading = await getisFinishedLoadingCashflow();
      if (!isFinishedLoading) {
        if (attempts >= maxAttempts) {
          stopPolling();
          try {
            handleError();
          } catch (error) {
            console.error('Error in handleError callback (max attempts):', error);
            DDLogs.error('HandleError callback failed on max attempts', { originalError: error, jobId });
          }
        }
        return;
      }
      // Check if OTS results are ready
      const otsResults = await getOtsResultsByJobId(jobId);

      // Handle maximum attempts reached or explicit error from API
      if (attempts >= maxAttempts || otsResults.error) {
        stopPolling();
        DDLogs.error('OTS results polling failed', {
          attempts,
          maxAttempts,
          error: otsResults.error,
          jobId,
        });
        try {
          handleError();
        } catch (error) {
          console.error('Error in handleError callback (OTS results failed):', error);
          DDLogs.error('HandleError callback failed on OTS results', { originalError: error, jobId });
        }
        return;
      }

      if (otsResults.isLoading) {
        return;
      }

      insights.value = otsResults.insights;
      stopPolling();
      onSuccess();
    } catch (error) {
      DDLogs.error('Network error during insights polling', {
        error,
        attempts,
        maxAttempts,
        jobId,
      });

      // Only stop polling if maximum attempts reached
      if (attempts >= maxAttempts) {
        stopPolling();
        try {
          handleError();
        } catch (error) {
          console.error('Error in handleError callback (network error):', error);
          DDLogs.error('HandleError callback failed on network error', { originalError: error, jobId });
        }
      }
    }
  };

  const stopPolling = () => {
    isPolling.value = false;
    clearInterval(polling);
  };

  const polling = setInterval(checkOtsResults, interval);
  checkOtsResults();
};

const populateInsights = async (jobId: string) => {
  const data = await getOtsResultsByJobId(jobId);
  insights.value = data.insights;
  return data.insights;
};

export const useInsightsService = () => {
  provide('insights', insights);
  provide('isPolling', isPolling);
  provide('populateInsights', populateInsights);
  provide('updateIsPolling', updateIsPolling);
  provide('startPollingByJobId', startPollingByJobId);
};

export const useInsights = (): UseInsights => ({
  insights: inject('insights')!,
  isPolling: inject('isPolling')!,
  populateInsights: inject('populateInsights')!,
  updateIsPolling: inject('updateIsPolling')!,
  startPollingByJobId: inject('startPollingByJobId')!,
});
